import { OnInit, Component } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { LOCAL_STORAGE } from '../../core/constants';
import { JwtHelperService } from '@auth0/angular-jwt';
import { AccountService } from '../shared/account.service';
import { ValidationUtils } from 'src/app/shared/validation.utils';
import { Client } from 'src/app/shared/models/Client';
import { Company } from 'src/app/shared/models/Company';
import { Country } from 'src/app/shared/models/Country';
import { TranslateService } from '@ngx-translate/core';
import { Option } from 'src/app/shared/form-group-select/form-group-select.component';
import { Village } from 'src/app/shared/models/Village';
import { forkJoin } from 'rxjs';

@Component({
    selector: 'app-details',
    templateUrl: './details.component.html',
    styleUrls: ['./details.component.css'],
    standalone: false
})
export class DetailsComponent implements OnInit {

  public currentLanguage: any;
  public clientForm: FormGroup;
  public companyForm: FormGroup;
  public clientErrorArray = [];
  public companyErrorArray = [];
  public chosenCountry: Country;
  public chosenVillage: Village;
  public currentCompany: Company;
  public newPassword: string;
  public newPasswordRepeat: string;
  public newPasswordErrorMsg = '';
  public newPasswordRepeatErrorMsg = '';
  public dataFetched = false;
  public titleOptions: Option[] = [
    { value: 'Herr', label: 'register.herr' },
    { value: 'Frau', label: 'register.frau' }
  ];
  public missingVillageErrorMsg = '';

  public locationGroupClass = 'col';

  private _clientId: number;
  private _newClient: Client = new Client();
  private _newCompany: Company;

  constructor(private _toastr: ToastrService, private _formBuilder: FormBuilder, public jwtHelper: JwtHelperService,
    private _accountService: AccountService, private _translate: TranslateService) {
    this.currentLanguage = this._translate.currentLang;
    this.clientForm = this._formBuilder.group({
      title: ['Herr', <any>Validators.required],
      firstName: ['', [<any>Validators.required, <any>Validators.minLength(2)]],
      lastName: ['', [<any>Validators.required, <any>Validators.minLength(2)]],
      firstEmail: [{ value: '', disable: true }],
      secondEmail: ['', <any>Validators.email],
      firstPhone: ['', <any>Validators.required],
      secondPhone: ['', <any>Validators.minLength(5)],
      language: ['de', [<any>Validators.required]],
      newsletter: [false]
    });

    this.companyForm = this._formBuilder.group({
      name: ['', [<any>Validators.required, <any>Validators.minLength(2)]],
      firstEmail: ['', [<any>Validators.required, <any>Validators.email]],
      secondEmail: ['', <any>Validators.email],
      firstPhone: ['', <any>Validators.required],
      secondPhone: [''],
      fiscalCode: ['', <any>Validators.required],
      vat: [''],
      pecEmail: ['', <any>Validators.email],
      isdNumber: [''],
      address: ['', <any>Validators.required],
      postalCode: ['', <any>Validators.required],
      city: ['', <any>Validators.required],
      village: [''],
      country: [''],
    });
  }

  ngOnInit() {
    const accessToken = localStorage.getItem(LOCAL_STORAGE.ACCESS_TOKEN);
    const tokenData = this.jwtHelper.decodeToken(accessToken).data;
    this._clientId = tokenData.id;

    this._accountService.getSpecificClientById(this._clientId).subscribe({
      next: (result) => {
        this.clientForm.patchValue(result);
        if (result.company != null) {
          this.currentCompany = result.company;
          this.companyForm.patchValue(result.company);
          this._newCompany = result.company;
        }
        if (this.companyForm.controls['firstEmail'].value === '') {
          this.companyForm.controls['firstEmail'].setValue(this.clientForm.controls['firstEmail'].value);
        }
        this._newClient = result;
        this.dataFetched = true;
      },
      error: () => console.log('Unable to retrieve client data'),
    });

    this.companyForm.controls['fiscalCode'].valueChanges.subscribe(currentValue => {
      if (typeof currentValue === 'string' && /[a-z]/.test(currentValue)) {
        this.companyForm.controls['fiscalCode'].setValue(currentValue.toUpperCase(), { emitEvent: false });
      }
    })
  }
  onSubmitClientForm() {
    this.missingVillageErrorMsg = '';
    if (this.clientForm.valid && this.companyForm.valid) {
      this._newClient = Client.fromDto(this.clientForm.value, this._newClient);
      if (this.clientForm.value.newPassword !== undefined || this.clientForm.value.newPassword !== '') {
        this._newClient.password = this.clientForm.value.newPassword;
      }

      if (!this.chosenCountry) {
        this.locationGroupClass = 'col border-red';
        this.missingVillageErrorMsg = this._translate.instant('account.errorMsg.missingCountry');
        return;
      }
      if (this.chosenCountry.id === 1 && !this.chosenVillage) {
        this.locationGroupClass = 'col border-red';
        this.missingVillageErrorMsg = this._translate.instant('account.errorMsg.missingVillage');
        return;
      }
      this.missingVillageErrorMsg = null;
      this.locationGroupClass = 'col';

      this._newCompany = Company.fromDto(this.companyForm.value, this._newCompany);
      this._newCompany.country = this.chosenCountry;
      this._newCompany.village = this.chosenVillage;
      this._newClient.company = this._newCompany;

      forkJoin([
        this._accountService.updateClient(this._newClient),
        this._accountService.updateCompany(this._newClient.id, this._newClient.company)
      ]).subscribe({
        next: () => this._toastr.success(this._translate.instant('account.clientUpdateSuccess')),
        error: () => this._toastr.error(this._translate.instant('account.updateError'))
      })
    } else {
      this._toastr.error(this._translate.instant('account.updateError'));
    }
    this.clientErrorArray = ValidationUtils.getFormValidationErrors(this.clientForm, 'account');
    this.companyErrorArray = ValidationUtils.getFormValidationErrors(this.companyForm, 'account');
    if (this.companyErrorArray["fiscalCode"] !== null && this.companyForm.controls['fiscalCode'].value !== '') {
      this.companyErrorArray["fiscalCode"] = this._translate.instant('account.errorMsg.fiscalCode');
    }
  }

  onCountryChosen(event) {
    this.chosenCountry = event;
    if (this.chosenCountry.id === 1) {
      if (this.companyForm.controls['isdNumber'].value === '') {
        this.companyForm.controls['isdNumber'].setValue('0000000');
      } else {
        this.companyForm.controls['isdNumber'].setValue('XXXXXXX');
      }
      this.companyForm.controls['fiscalCode'].setValidators([Validators.required, Validators.pattern("^([A-Z]{6}[0-9]{2}[A-Z][0-9]{2}[A-Z][0-9]{3}[A-Z]|[0-9]{11})$")]);
      this.companyForm.controls['fiscalCode'].updateValueAndValidity();
    } else {
      this.companyForm.controls['fiscalCode'].setValidators(Validators.required);
      this.companyForm.controls['fiscalCode'].updateValueAndValidity();
    }
  }

  onMailChange(event) {
    if (this.companyForm.controls['firstEmail'].value === '') {
      this.companyForm.controls['firstEmail'].setValue(event.target.value);
    }
  }

  onPhoneChange(event) {
    if (this.companyForm.controls['firstPhone'].value === '') {
      this.companyForm.controls['firstPhone'].setValue(event.target.value);
    }
  }

  updateClientPassword() {
    this.newPasswordErrorMsg = '';
    this.newPasswordRepeatErrorMsg = '';
    if (!this.newPassword) {
      this.newPasswordErrorMsg = this._translate.instant('account.errorMsg.required');
      return;
    }
    if (!this.newPasswordRepeat) {
      this.newPasswordRepeatErrorMsg = this._translate.instant('account.errorMsg.required');
      return;
    }
    if (this.newPassword !== this.newPasswordRepeat) {
      this.newPasswordRepeatErrorMsg = this._translate.instant('account.errorMsg.passwordNotEqual');
      return;
    }
    this._accountService.changeClientPassword(this._clientId, this.newPassword).subscribe({
      next: () => {
        this.newPassword = '';
        this.newPasswordRepeat = '';
        this._toastr.success(this._translate.instant('account.passwordSuccessUpdate'));
      },
      error: () => console.log('Unable to change password'),
    });
  }

  onInputChange(name, company: boolean) {
    if (company) {
      if (this.companyErrorArray[name] !== null) {
        this.companyErrorArray[name] = null;
      }
    } else {
      if (this.clientErrorArray[name] !== null) {
        this.clientErrorArray[name] = null;
      }
    }
  }

}
